import { useState, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import PersonIcon from '@mui/icons-material/Person'
import StorageIcon from '@mui/icons-material/Storage'
import { useQuery } from '@tanstack/react-query'
import { workflowDesignerApi } from '@api/endpoints/workflowDesigner'
import {
  FlowVersionSummaryDto,
  FlowVersionStatus,
  getFlowVersionStatusLabel,
} from '@features/adventures/types/workflow.types'

interface FlowVersionHistoryDialogProps {
  open: boolean
  onClose: () => void
  adventureId: string
  currentVersionNumber: number | null
  getCurrentWorkingState: (() => {
    flowData: {
      nodes: unknown[]
      edges: unknown[]
    }
    description?: string
  }) | null
}

export function FlowVersionHistoryDialog({
  open,
  onClose,
  adventureId,
  currentVersionNumber,
  getCurrentWorkingState,
}: FlowVersionHistoryDialogProps) {
  const [confirmLoadVersion, setConfirmLoadVersion] = useState<number | null>(null)
  const [downloadingVersion, setDownloadingVersion] = useState<number | null>(null)

  // Fetch version history
  const {
    data: versionHistory = [] as FlowVersionSummaryDto[],
    isLoading: isLoadingHistory,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: ['workflow-history', adventureId],
    queryFn: () => workflowDesignerApi.getFlowHistory(adventureId),
    enabled: open, // Only fetch when dialog is open
  })

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // Download saved version from API
  const handleDownloadSaved = useCallback(async (versionNumber: number) => {
    setDownloadingVersion(versionNumber)
    try {
      const flowVersion = await workflowDesignerApi.getFlow(adventureId, versionNumber)
      const json = JSON.stringify(flowVersion, null, 2)
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `workflow-${adventureId}-v${versionNumber}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download version:', error)
    } finally {
      setDownloadingVersion(null)
    }
  }, [adventureId])

  // Download current working state from store
  const handleDownloadWorking = useCallback(() => {
    if (!getCurrentWorkingState) return
    const state = getCurrentWorkingState()
    const json = JSON.stringify(state, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `workflow-${adventureId}-working.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [adventureId, getCurrentWorkingState])

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Version History
          {currentVersionNumber !== null && currentVersionNumber > 0 && (
            <Chip
              label={`Current: v${currentVersionNumber}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : versionHistory.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No saved versions found
            </Typography>
          ) : (
            <List disablePadding>
              {versionHistory.map((version) => {
                const statusLabel = getFlowVersionStatusLabel(version.status)
                const isCurrentVersion = version.versionNumber === currentVersionNumber
                const hasWorkingState = getCurrentWorkingState !== null
                const isDownloading = downloadingVersion === version.versionNumber

                return (
                  <ListItem
                    key={version.id}
                    disablePadding
                    divider
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {/* Download Saved button - always shown */}
                        <Tooltip title="Download Saved">
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDownloadSaved(version.versionNumber)
                              }}
                              disabled={isDownloading}
                            >
                              {isDownloading ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {/* Download Working button - only for current version when workflow is loaded */}
                        {isCurrentVersion && hasWorkingState && (
                          <Tooltip title="Download Working (Unsaved)">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDownloadWorking()
                              }}
                              color="warning"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  >
                    <ListItemButton
                      onClick={() => setConfirmLoadVersion(version.versionNumber)}
                      sx={{ py: 1.5, pr: isCurrentVersion && hasWorkingState ? 12 : 8 }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body1" fontWeight={500}>
                              Version {version.versionNumber}
                            </Typography>
                            <Chip
                              label={statusLabel}
                              size="small"
                              color={
                                version.status === FlowVersionStatus.Published ? 'success' :
                                version.status === FlowVersionStatus.Testing ? 'info' :
                                version.isCompiled ? 'warning' : 'default'
                              }
                            />
                            {version.isCompiled && version.status === FlowVersionStatus.Draft && (
                              <Chip label="Compiled" size="small" variant="outlined" />
                            )}
                            {isCurrentVersion && (
                              <Chip label="Current" size="small" color="primary" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                            {version.description && (
                              <Typography variant="caption" component="span" sx={{ fontStyle: 'italic' }}>
                                {version.description}
                              </Typography>
                            )}
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" component="span">
                                {version.createdByUserName}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" component="span">
                              Created: {new Date(version.createdAt).toLocaleString()}
                            </Typography>
                            {version.updatedAt !== version.createdAt && (
                              <Typography variant="caption" color="text.secondary" component="span">
                                Updated: {new Date(version.updatedAt).toLocaleString()}
                              </Typography>
                            )}
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <StorageIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" component="span">
                                {formatBytes(version.compressedSize)} compressed / {formatBytes(version.uncompressedSize)} raw
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => refetchHistory()} disabled={isLoadingHistory}>
            Refresh
          </Button>
          <Button onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Load Version Dialog */}
      <Dialog
        open={confirmLoadVersion !== null}
        onClose={() => setConfirmLoadVersion(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Load Version {confirmLoadVersion}?</DialogTitle>
        <DialogContent>
          <Typography>
            This will replace your current workflow with the saved version. Any unsaved changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLoadVersion(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (confirmLoadVersion !== null) {
                // Load the version - this is handled by the parent component
                // For now, close the dialogs - the parent will handle the actual loading
                const versionToLoad = confirmLoadVersion
                setConfirmLoadVersion(null)
                onClose()
                // Dispatch a custom event that the parent can listen to
                window.dispatchEvent(new CustomEvent('flow-load-version', { detail: { version: versionToLoad } }))
              }
            }}
          >
            Load Version
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
